import { VisitContext, type DataContext, type Company, type Site } from '../schemas';
import type { ExpandableCatalog } from '../models/catalog';
import { formatVisitControlPeriod } from '../../utils/periodicity';
import { DEFAULT_SETTINGS } from '../repos/settingsRepo';
import { filterPersonnelForSite } from '@/data/personnel/personnelScope';
import { formatIsoDate } from '@/utils/date';
import {
  buildCompanyContext,
  buildDefaultUnitChecks,
  buildMetricsContext,
  buildScheduleContext,
  buildSiteContext,
  recipientsFromPersonnel,
} from './templateContext/builders';

interface BuildBaseContextOptions {
  company: Company;
  site?: Site;
  variables: Record<string, unknown>;
  reportId: string;
  previousReport?: { id: string; visitDate: string } | null;
  catalogs?: Record<string, ExpandableCatalog>;
}

export function buildBaseTemplateContext({
  company,
  site,
  variables,
  reportId,
  previousReport,
  catalogs,
}: BuildBaseContextOptions): DataContext {
  const inspectorName = String(variables.inspectorName ?? 'Ispettore');
  const rawVisitDate =
    typeof variables.visitDate === 'string' && variables.visitDate
      ? variables.visitDate
      : typeof variables.today === 'string'
        ? variables.today
        : formatIsoDate(new Date());
  const schedule = buildScheduleContext(site, rawVisitDate, previousReport);
  const frequencyMonths = schedule?.frequencyMonths;
  const visitPeriod =
    schedule?.controlPeriod ?? formatVisitControlPeriod(rawVisitDate, frequencyMonths);
  const resolvedVariables = {
    ...variables,
    visitDate: rawVisitDate,
    visitPeriod,
    nostroRecapito: variables.nostroRecapito ?? DEFAULT_SETTINGS.nostroRecapito,
  };
  const siteContext = buildSiteContext(site, schedule, catalogs);
  const unitChecks = buildDefaultUnitChecks(site);
  const scorecard = {
    byDate: [
      {
        id: `score-${reportId}`,
        date: rawVisitDate,
      },
    ],
  };

  const visitContext = VisitContext.parse({
    date: rawVisitDate,
    period: visitPeriod,
    frequencyMonths,
    inspector: inspectorName,
    unitChecks,
    scorecard,
  });

  const siteInfra = visitContext.infraVerification;
  const metrics = buildMetricsContext(siteContext, schedule, previousReport);

  const nostroRecapito =
    typeof resolvedVariables.nostroRecapito === 'string'
      ? resolvedVariables.nostroRecapito
      : DEFAULT_SETTINGS.nostroRecapito;

  const siteIdForScope = site?.id ?? undefined;
  const scopedPersonnel = filterPersonnelForSite(company.personnel ?? [], siteIdForScope);
  const scopedCompany = {
    ...company,
    personnel: scopedPersonnel,
  };

  const baseContext: DataContext = {
    company: buildCompanyContext(scopedCompany, nostroRecapito, site),
    site: siteContext,
    metrics,
    report: { id: reportId },
    signatures: { inspectorName },
    visit: visitContext,
    variables: resolvedVariables,
  } as DataContext;

  if (siteInfra) {
    (baseContext as Record<string, unknown>).siteInfra = siteInfra;
  }

  return baseContext;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.prototype.toString.call(value) === '[object Object]'
  );
}

export function mergeTemplateContext(
  base: DataContext,
  overrides?: Partial<DataContext>
): DataContext {
  if (!overrides) return base;

  const result: Record<string, unknown> = { ...base };

  for (const [key, value] of Object.entries(overrides)) {
    if (value === undefined) continue;
    const current = result[key];
    if (isPlainObject(current) && isPlainObject(value)) {
      const mergedObject = mergeTemplateContext(
        current as DataContext,
        value as DataContext
      ) as unknown as Record<string, unknown>;
      result[key] = mergedObject;
    } else {
      result[key] = value;
    }
  }

  return result as DataContext;
}

export { recipientsFromPersonnel };
export type TemplateContextOverrides = Partial<DataContext>;
